import { ChevronDown, Pencil, Plus, Share2, Trash2, Users, X } from 'lucide-react';
import { useState } from 'react';
import { displayName, newPerson } from '../../model/people';
import { removeGroup, upsertGroup, upsertPerson } from '../../model/tree';
import type { Group, GroupKind } from '../../model/types';
import { newId } from '../../model/people';
import { Avatar, Field, Modal, PersonRow, Select } from '../components/common';
import { PersonPicker } from '../components/PersonPicker';
import { GROUP_COLORS, GROUP_KIND_LABELS } from '../labels';
import { closeDialog, commit, confirmAction, focusPerson, getState, hasPanel, openDialog, replaceDialog, selectPerson, setState, showPerson, useApp } from '../store';

export function GroupsView() {
  const tree = useApp((s) => s.tree)!;
  const [kind, setKind] = useState<GroupKind | ''>('');
  const groups = Object.values(tree.groups)
    .filter((g) => !kind || g.kind === kind)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="page">
      <div className="page-inner" style={{ maxWidth: 1100 }}>
        <div className="page-head">
          <div>
            <h1 style={{ fontSize: 22 }}>Groups</h1>
            <p className="muted" style={{ marginTop: 4 }}>
              Friend groups, found families, households, and communities. People can be in any number of groups.
            </p>
          </div>
          <div className="row">
            <select className="select" style={{ width: 'auto' }} value={kind} onChange={(e) => setKind(e.target.value as GroupKind | '')} aria-label="Filter by kind">
              <option value="">All kinds</option>
              {Object.entries(GROUP_KIND_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
            <button className="btn primary" onClick={() => openDialog({ type: 'edit-group', groupId: null })}>
              <Plus size={15} /> New group
            </button>
          </div>
        </div>
        {groups.length === 0 ? (
          <div className="card-box empty">
            <div>
              <Users size={28} />
              <h3>No groups yet</h3>
              <p style={{ marginTop: 6 }}>Create a group for a friend circle, a found family, a household, or any community.</p>
            </div>
          </div>
        ) : (
          <div className="group-grid">
            {groups.map((g) => (
              <GroupCard key={g.id} group={g} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** One group: who's in it at a glance, and the full member list with roles on demand. */
function GroupCard({ group: g }: { group: Group }) {
  const tree = useApp((s) => s.tree)!;
  const [showMembers, setShowMembers] = useState(false);
  const members = g.members.filter((m) => tree.people[m.personId]);
  const count = `${members.length} ${members.length === 1 ? 'member' : 'members'}`;
  return (
    <div className="group-card">
      <div className="stripe" style={{ background: g.color }} />
      <div className="inner">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <h3>{g.name}</h3>
          <span className="badge group-tint" style={{ '--gc': g.color } as React.CSSProperties}>
            {GROUP_KIND_LABELS[g.kind]}
          </span>
        </div>
        {g.description && <p className="muted small-text">{g.description}</p>}
        {members.length > 0 ? (
          <button className="member-toggle" aria-expanded={showMembers} onClick={() => setShowMembers((x) => !x)} title={showMembers ? 'Hide members' : 'Show members and roles'}>
            <span className="avatar-stack" aria-hidden>
              {members.slice(0, 10).map((m) => (
                <Avatar key={m.personId} person={tree.people[m.personId]} size="sm" />
              ))}
              {members.length > 10 && <span className="avatar sm" style={{ background: 'var(--text-faint)' }}>+{members.length - 10}</span>}
            </span>
            <span className="small-text muted row" style={{ gap: 4 }}>
              {count} <ChevronDown size={14} className={showMembers ? 'flip' : undefined} />
            </span>
          </button>
        ) : (
          <div className="small-text muted">No members yet</div>
        )}
        {showMembers && (
          <div className="member-list">
            {members.map((m) => (
              <PersonRow key={m.personId} person={tree.people[m.personId]} meta={m.role} onClick={() => showPerson(m.personId)} />
            ))}
          </div>
        )}
        <div className="row" style={{ marginTop: 'auto' }}>
          <button className="btn small" onClick={() => openDialog({ type: 'edit-group', groupId: g.id })}>
            <Pencil size={14} /> Edit
          </button>
          {members[0] && (
            <button className="btn small ghost" onClick={() => focusPerson(members[0].personId, { view: 'network', select: false })}>
              <Share2 size={14} /> Show in network
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A group opened from elsewhere (someone's details, the network). It's laid out like the
 * person details it opens from, with the same information and actions as the Groups tab card.
 */
export function GroupDetails({ groupId }: { groupId: string }) {
  const tree = useApp((s) => s.tree)!;
  const g = tree.groups[groupId];
  if (!g) {
    return (
      <Modal title="Group not found" footer={<button className="btn" onClick={closeDialog}>Close</button>}>
        <p className="muted">This group has been deleted.</p>
      </Modal>
    );
  }
  const members = g.members.filter((m) => tree.people[m.personId]);
  // A member opens in the chart's side panel if there is one, otherwise their details replace
  // this dialog (Escape goes back to whoever was open before).
  const openMember = (id: string) => {
    if (hasPanel(getState().view)) {
      closeDialog();
      selectPerson(id);
    } else replaceDialog({ type: 'person-details', personId: id });
  };
  return (
    <Modal className="details-modal" ariaLabel={`${g.name} (group)`}>
      <div className="panel in-dialog">
        <div className="panel-head">
          <span className="avatar lg group-avatar" style={{ background: g.color }} aria-hidden>
            <Users size={24} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2>{g.name}</h2>
            <div className="sub">
              {GROUP_KIND_LABELS[g.kind]} · {members.length} {members.length === 1 ? 'member' : 'members'}
            </div>
          </div>
          <button className="btn icon ghost" onClick={closeDialog} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="panel-actions">
          <button className="btn small primary" onClick={() => replaceDialog({ type: 'edit-group', groupId: g.id })}>
            <Pencil size={14} /> Edit
          </button>
          {members[0] && (
            <button
              className="btn small"
              onClick={() => {
                closeDialog();
                focusPerson(members[0].personId, { view: 'network', select: false });
              }}
            >
              <Share2 size={14} /> Show in network
            </button>
          )}
        </div>
        {g.description && (
          <section className="panel-section">
            <h3>About</h3>
            <p className="small-text" style={{ whiteSpace: 'pre-wrap' }}>{g.description}</p>
          </section>
        )}
        <section className="panel-section">
          <h3>Members</h3>
          {members.map((m) => (
            <PersonRow key={m.personId} person={tree.people[m.personId]} meta={m.role} onClick={() => openMember(m.personId)} />
          ))}
          {!members.length && <p className="faint small-text">No members yet.</p>}
        </section>
      </div>
    </Modal>
  );
}

export function GroupEditor({ groupId }: { groupId: string | null }) {
  const tree = useApp((s) => s.tree)!;
  const existing = groupId ? tree.groups[groupId] : undefined;
  const [g, setG] = useState<Group>(
    () =>
      structuredClone(existing) ?? {
        id: newId('g'),
        name: '',
        kind: 'friend-group',
        description: '',
        color: GROUP_COLORS[Object.keys(tree.groups).length % GROUP_COLORS.length],
        members: [],
      },
  );
  const [pendingNew, setPendingNew] = useState<ReturnType<typeof newPerson>[]>([]);
  const set = (patch: Partial<Group>) => setG((x) => ({ ...x, ...patch }));
  const people = { ...tree.people, ...Object.fromEntries(pendingNew.map((p) => [p.id, p])) };

  const save = () => {
    let t = getState().tree!;
    for (const p of pendingNew) if (g.members.some((m) => m.personId === p.id)) t = upsertPerson(t, p);
    commit(upsertGroup(t, { ...g, name: g.name.trim() || 'Untitled group' }));
    closeDialog();
  };

  return (
    <Modal
      title={existing ? `Edit ${existing.name}` : 'New group'}
      footer={
        <>
          {existing && (
            <button
              className="btn danger left"
              onClick={() =>
                confirmAction({
                  title: `Delete ${existing.name}?`,
                  body: 'The group is deleted; its members and their relationships are not.',
                  confirmLabel: 'Delete group',
                  danger: true,
                  onConfirm: () => {
                    commit(removeGroup(getState().tree!, existing.id));
                    closeDialog();
                  },
                })
              }
            >
              <Trash2 size={15} /> Delete group
            </button>
          )}
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="grid-2">
          <Field label="Name">
            <input className="input" value={g.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g., House of Moore, Book Club" autoFocus />
          </Field>
          <Field label="Kind">
            <Select value={g.kind} onChange={(kind) => set({ kind })} options={GROUP_KIND_LABELS} />
          </Field>
        </div>
        <Field label="Color">
          <div className="row wrap" role="radiogroup" aria-label="Group color">
            {GROUP_COLORS.map((c) => (
              <button
                key={c}
                role="radio"
                aria-checked={g.color === c}
                aria-label={c}
                onClick={() => set({ color: c })}
                style={{ width: 26, height: 26, borderRadius: '50%', background: c, border: g.color === c ? '3px solid var(--text)' : '2px solid transparent', cursor: 'pointer' }}
              />
            ))}
          </div>
        </Field>
        <Field label="Description">
          <textarea className="textarea" style={{ minHeight: 60 }} value={g.description} onChange={(e) => set({ description: e.target.value })} />
        </Field>
        <div className="section-title">Members ({g.members.filter((m) => people[m.personId]).length})</div>
        <PersonPicker
          tree={{ ...tree, people }}
          value={null}
          exclude={g.members.map((m) => m.personId)}
          placeholder="Add a member: search or type a new name…"
          onChange={(id) => id && set({ members: [...g.members, { personId: id }] })}
          onCreate={(text) => {
            const [given, ...rest] = text.split(' ');
            const p = newPerson({ names: [{ given, surname: rest.join(' '), type: 'name' }] });
            setPendingNew((x) => [...x, p]);
            set({ members: [...g.members, { personId: p.id }] });
          }}
        />
        <div className="stack tight">
          {g.members.map((m, i) =>
            people[m.personId] ? (
              <div key={m.personId} className="row">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <PersonRow person={people[m.personId]} onClick={() => tree.people[m.personId] && setState({ selectedId: m.personId })} />
                </div>
                <input
                  className="input"
                  style={{ width: 150 }}
                  placeholder="Role (optional)"
                  aria-label={`Role for ${displayName(people[m.personId])}`}
                  value={m.role ?? ''}
                  onChange={(e) => set({ members: g.members.map((x, j) => (j === i ? { ...x, role: e.target.value || undefined } : x)) })}
                />
                <button className="btn icon small ghost danger" aria-label="Remove member" onClick={() => set({ members: g.members.filter((_, j) => j !== i) })}>
                  <Trash2 size={14} />
                </button>
              </div>
            ) : null,
          )}
        </div>
      </div>
    </Modal>
  );
}
